import { ArrowRight, Shuffle, Target } from "lucide-react";
import { useState } from "react";
import type { Preferences } from "../types/preferences";
import type { LearningPath, LearningPathId } from "../types";

export function Onboarding({
  paths,
  onComplete,
  onStartRun,
}: {
  paths: LearningPath[];
  onComplete: (patch: Pick<Preferences, "primaryPathId" | "dailyGoalItems" | "onboardedAt">) => void;
  onStartRun: (pathId: LearningPathId | "all") => void;
}) {
  const [step, setStep] = useState(0);
  const [selectedPathId, setSelectedPathId] = useState<LearningPathId>(paths[0]?.id ?? "hagen-jura");
  const [mixMode, setMixMode] = useState(false);
  const [dailyGoalItems, setDailyGoalItems] = useState(10);
  const selectedPath = paths.find((path) => path.id === selectedPathId) ?? paths[0];

  function finish() {
    onComplete({
      primaryPathId: mixMode ? null : selectedPathId,
      dailyGoalItems,
      onboardedAt: new Date().toISOString(),
    });
    onStartRun(mixMode ? "all" : selectedPathId);
  }

  return (
    <section className="onboarding-panel" aria-label="Onboarding">
      {step === 0 ? (
        <>
          <div className="panel-heading">
            <span className="eyebrow">Start</span>
            <h2>Was bereitest du vor?</h2>
          </div>
          <div className="onboarding-options">
            {paths.map((path) => (
              <button
                className={`path-card ${!mixMode && selectedPathId === path.id ? "is-active" : ""}`}
                key={path.id}
                onClick={() => {
                  setSelectedPathId(path.id);
                  setMixMode(false);
                  setStep(1);
                }}
                type="button"
              >
                <strong>{path.label}</strong>
                <span>{path.description}</span>
              </button>
            ))}
            <button
              className="path-card path-card-mix"
              onClick={() => {
                setMixMode(true);
                setStep(1);
              }}
              type="button"
            >
              <strong>
                <Shuffle size={16} /> Alle Pfade · Bunter Mix
              </strong>
              <span>Querbeet aus allen Themen — abwechslungsreich, ideal zum Warmlaufen und Wiederholen.</span>
            </button>
          </div>
        </>
      ) : null}

      {step === 1 ? (
        <>
          <div className="panel-heading">
            <span className="eyebrow">Tagesziel</span>
            <h2>Wie gross soll eine normale Session sein?</h2>
          </div>
          <div className="onboarding-options compact">
            {[5, 10, 20].map((goal) => (
              <button
                className={`chip ${dailyGoalItems === goal ? "is-active" : ""}`}
                key={goal}
                onClick={() => {
                  setDailyGoalItems(goal);
                  setStep(2);
                }}
                type="button"
              >
                {goal} Aufgaben
              </button>
            ))}
          </div>
        </>
      ) : null}

      {step === 2 ? (
        <>
          <div className="panel-heading">
            <span className="eyebrow">Erste Frage</span>
            <h2>{mixMode ? "Bunter Mix" : selectedPath?.label ?? "Dein Pfad"} wartet.</h2>
          </div>
          <button className="primary-action onboarding-start" onClick={finish} type="button">
            <Target size={18} />
            Erste Frage starten
            <ArrowRight size={18} />
          </button>
        </>
      ) : null}
    </section>
  );
}
